import { useRef, useMemo, useState, Suspense } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import { stressStore, crisisLevel, useStress } from "../store";
import { useTour } from "./tour";

function statusLabel(c) {
  return c < 0.25 ? "Stable" : c < 0.55 ? "Strained" : c < 0.8 ? "Critical" : "Collapse";
}

function tierStats(key, s) {
  const bs = Math.min(1, s.phd * 0.55 + s.funding * 0.55);
  const crisis = crisisLevel(s);
  if (key === "base")
    return {
      title: "BASE · Adjuncts & TAs",
      accent: "#ff4d63",
      lines: [
        "Population:  110",
        `Precarity:   ${Math.round(bs * 100)}%`,
        `Pace:        ${(0.7 + bs * 4).toFixed(1)}x`,
        `Status:      ${statusLabel(crisis)}`,
      ],
      facts: [
        "~75% of U.S. faculty are now contingent",
        "Median pay ≈ $3,500 per course",
        "~25% rely on public assistance",
      ],
      source: "AAUP 2023 · CAW Adjunct Report 2020",
    };
  if (key === "middle")
    return {
      title: "MIDDLE · Tenure",
      accent: "#3f8cff",
      lines: [
        "Population:  30",
        `Security:    ${Math.round((1 - s.budget) * 100)}%`,
        `Contraction: ${Math.round(s.budget * 100)}%`,
        `Status:      ${statusLabel(crisis)}`,
      ],
      facts: [
        "Only ~24% hold tenure-track lines",
        "Median full-professor pay ≈ $121k",
        "Tenure lines down ~26% since 1987",
      ],
      source: "AAUP Faculty Compensation Survey 2022–23",
    };
  return {
    title: "APEX · Administration",
    accent: "#f5c14e",
    lines: [
      "Population:  10",
      "Control:     100%",
      `Extraction:  ${Math.round((0.6 + crisis * 0.4) * 100)}%`,
      "Insulation:  High",
    ],
    facts: [
      "Admin roles grew ~60% (1993–2009)",
      "Managers now outnumber faculty",
      "Top presidents earn $1M+ per year",
    ],
    source: "Delta Cost Project · Chronicle of Higher Ed 2023",
  };
}

function TierCardDOM({ tierKey }) {
  // subscribe to all three axes so stats stay live as sliders / timeline change
  useStress((s) => `${s.phd},${s.budget},${s.funding}`);
  const live = tierStats(tierKey, stressStore.getState());
  return (
    <div className="af-tiercard" style={{ borderColor: live.accent }} data-testid={`tier-card-${tierKey}`}>
      <div className="af-tiercard-title" style={{ color: live.accent }}>
        <span className="af-tiercard-dot" style={{ background: live.accent }} />
        {live.title}
      </div>
      <div className="af-tiercard-body">
        {live.lines.map((l, i) => (
          <div className="af-tiercard-line" key={i}>
            <span>{l.split(":")[0]}</span>
            <b>{l.split(":").slice(1).join(":").trim()}</b>
          </div>
        ))}
      </div>
      <div className="af-tiercard-facts">
        <div className="af-tiercard-facts-head">The Research</div>
        {live.facts.map((f, i) => (
          <div className="af-tiercard-fact" key={i} style={{ "--acc": live.accent }}>
            {f}
          </div>
        ))}
      </div>
      <div className="af-tiercard-source">{live.source}</div>
    </div>
  );
}

function TierCard({ tierKey, position }) {
  return (
    <Html position={position} center distanceFactor={9} zIndexRange={[40, 0]} pointerEvents="none">
      <TierCardDOM tierKey={tierKey} />
    </Html>
  );
}

const CARD_POS = {
  base: [-3.5, 1.5, 1.6],
  middle: [-3.7, 2.9, 1.6],
  apex: [3.1, 4.4, 1.0],
};

function GlassFrustum({
  radiusBottom,
  radiusTop,
  height,
  y,
  color,
  emissive = "#000000",
  emissiveIntensity = 0,
  meshRef,
  tierKey,
  onHover,
}) {
  const geo = useMemo(
    () => new THREE.CylinderGeometry(radiusTop, radiusBottom, height, 4, 1),
    [radiusBottom, radiusTop, height]
  );

  return (
    <mesh
      ref={meshRef}
      geometry={geo}
      position={[0, y, 0]}
      rotation={[0, Math.PI / 4, 0]}
      castShadow
      onPointerOver={(e) => {
        e.stopPropagation();
        onHover(tierKey);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        onHover(null);
      }}
    >
      <meshPhysicalMaterial
        color={color}
        emissive={emissive}
        emissiveIntensity={emissiveIntensity}
        transmission={0.92}
        thickness={2.2}
        roughness={0.32}
        metalness={0}
        ior={1.45}
        clearcoat={0.6}
        clearcoatRoughness={0.4}
        attenuationColor={color}
        attenuationDistance={2.4}
        transparent
        opacity={0.9}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

export default function Pyramid() {
  const baseRef = useRef();
  const baseGlow = useRef();
  const goldRefs = useRef([]);
  const [hovered, setHovered] = useState(
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("tier") : null
  );
  const tourFocus = useTour((t) => t.focusTier);

  const flecks = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 26; i++) {
      const r = 0.55 * Math.random();
      const a = Math.random() * Math.PI * 2;
      arr.push([
        Math.cos(a) * r,
        3.35 + Math.random() * 0.7,
        Math.sin(a) * r,
        0.02 + Math.random() * 0.035,
      ]);
    }
    return arr;
  }, []);

  useFrame((s) => {
    const crisis = crisisLevel(stressStore.getState());
    const t = s.clock.elapsedTime;
    if (baseRef.current) {
      const target = 1 + crisis * 0.9 + Math.sin(t * (2 + crisis * 6)) * crisis * 0.06;
      const next = THREE.MathUtils.lerp(baseRef.current.scale.x, target, 0.08);
      baseRef.current.scale.x = next;
      baseRef.current.scale.z = next;
      baseRef.current.scale.y = THREE.MathUtils.lerp(baseRef.current.scale.y, 1 + crisis * 0.25, 0.08);
    }
    if (baseGlow.current) {
      baseGlow.current.material.emissiveIntensity = THREE.MathUtils.lerp(
        baseGlow.current.material.emissiveIntensity,
        crisis * 1.4,
        0.08
      );
    }
    goldRefs.current.forEach((m, i) => {
      if (m) m.material.emissiveIntensity = 1.2 + Math.sin(t * 3 + i) * 0.6;
    });
  });

  return (
    <group position={[0, 0, 0]}>
      {/* BASE — Adjuncts / TAs : deep ruby-red crystal (swells) */}
      <GlassFrustum
        meshRef={(el) => {
          baseRef.current = el;
          baseGlow.current = el;
        }}
        tierKey="base"
        onHover={setHovered}
        radiusBottom={2.5}
        radiusTop={2.0}
        height={1.25}
        y={0.63}
        color="#b3122b"
        emissive="#ff2d4d"
        emissiveIntensity={0}
      />

      {/* MIDDLE — Tenure : sapphire blue */}
      <GlassFrustum
        tierKey="middle"
        onHover={setHovered}
        radiusBottom={1.95}
        radiusTop={1.5}
        height={1.0}
        y={1.75}
        color="#1a5fd0"
        emissive="#2f6bff"
        emissiveIntensity={0.06}
      />

      {/* MIDDLE — Tenure : emerald green */}
      <GlassFrustum
        tierKey="middle"
        onHover={setHovered}
        radiusBottom={1.5}
        radiusTop={1.05}
        height={0.9}
        y={2.7}
        color="#0f9d68"
        emissive="#19d089"
        emissiveIntensity={0.06}
      />

      {/* APEX — Admin : clear quartz with golden flecks */}
      <GlassFrustum
        tierKey="apex"
        onHover={setHovered}
        radiusBottom={1.05}
        radiusTop={0.0}
        height={1.2}
        y={3.75}
        color="#eef2f2"
        emissive="#fff4d6"
        emissiveIntensity={0.04}
      />

      {flecks.map((f, i) => (
        <mesh key={i} position={[f[0], f[1], f[2]]} ref={(el) => (goldRefs.current[i] = el)}>
          <icosahedronGeometry args={[f[3], 0]} />
          <meshStandardMaterial color="#f5c14e" emissive="#ffcf5a" emissiveIntensity={1.4} metalness={1} roughness={0.25} />
        </mesh>
      ))}

      <mesh position={[0, 3.12, 0]} rotation={[Math.PI / 2, 0, Math.PI / 4]}>
        <torusGeometry args={[1.02, 0.02, 8, 4]} />
        <meshStandardMaterial color="#f5c14e" emissive="#ffcf5a" emissiveIntensity={1.2} metalness={1} roughness={0.3} />
      </mesh>

      {(() => {
        const shown = tourFocus || hovered;
        return (
          shown && (
            <Suspense fallback={null}>
              <TierCard tierKey={shown} position={CARD_POS[shown]} />
            </Suspense>
          )
        );
      })()}
    </group>
  );
}
